import { useState, useEffect } from 'react'
import { Plus, Trash2, Pencil, X, AlertTriangle, Info, Loader2 } from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { gestaoService } from '../../services/gestaoService'
import toast from 'react-hot-toast'

// ── Modal criar / editar ──────────────────────────────────────────────────────
function ModalCategoria({ tipo, categoriaEditando, onClose, onSalvar }) {
  const isEdit = !!categoriaEditando
  const [nome, setNome] = useState(categoriaEditando?.nome || '')
  const [loading, setLoading] = useState(false)

  const handleSalvar = async () => {
    if (!nome.trim()) return toast.error('Informe o nome da categoria')
    setLoading(true)
    await onSalvar({ nome: nome.trim(), tipo_categoria: tipo })
    setLoading(false)
  }

  const corBtn = tipo === 'receita' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
  const titulo = isEdit
    ? `Editar Categoria de ${tipo === 'receita' ? 'Receita' : 'Despesa'}`
    : `Nova Categoria de ${tipo === 'receita' ? 'Receita' : 'Despesa'}`

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-800">{titulo}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5">
          <label className="block text-xs font-medium text-gray-600 mb-1">Nome *</label>
          <input
            autoFocus value={nome} onChange={e => setNome(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSalvar()}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-300"
            placeholder="Ex: Patrocínio, Veterinário..."
          />
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            Cancelar
          </button>
          <button onClick={handleSalvar} disabled={loading}
            className={`flex-1 px-4 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-50 ${corBtn}`}>
            {loading ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Modal confirmação / bloqueio de exclusão ──────────────────────────────────
function ModalExcluir({ categoria, uso, loading, onClose, onConfirmar }) {
  const emUso = uso !== null && uso.total > 0

  const titulo = emUso ? 'Não é possível excluir' : `Excluir "${categoria?.nome}"?`

  const mensagem = emUso
    ? `Esta categoria está vinculada a ${uso.total} lançamento(s) e não pode ser excluída.`
    : 'Tem certeza? Esta ação não pode ser desfeita.'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
          {emUso
            ? <Info className="w-5 h-5 text-blue-500 flex-shrink-0" />
            : <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
          }
          <h2 className="text-base font-semibold text-gray-800">{titulo}</h2>
        </div>

        <div className="px-6 py-5 space-y-3">
          {loading
            ? (
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Verificando uso da categoria...
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-600">{mensagem}</p>

                {/* Detalhes de uso */}
                {uso !== null && (
                  <div className="bg-gray-50 rounded-lg border border-gray-200 divide-y divide-gray-100">
                    <div className="flex justify-between px-4 py-2">
                      <span className="text-xs font-medium text-gray-500 uppercase">Categoria</span>
                      <span className="text-sm font-semibold text-gray-800">{categoria?.nome}</span>
                    </div>
                    <div className="flex justify-between px-4 py-2">
                      <span className="text-xs font-medium text-gray-500 uppercase">Tipo</span>
                      <span className="text-sm font-semibold text-gray-800">
                        {categoria?.tipo_categoria === 'receita' ? 'Receita' : 'Despesa'}
                      </span>
                    </div>
                    {uso.total > 0 && (
                      <>
                        {uso.receitas > 0 && (
                          <div className="flex justify-between px-4 py-2">
                            <span className="text-xs font-medium text-gray-500 uppercase">Contas a Receber</span>
                            <span className="text-sm font-semibold text-red-600">{uso.receitas} lançamento(s)</span>
                          </div>
                        )}
                        {uso.despesas > 0 && (
                          <div className="flex justify-between px-4 py-2">
                            <span className="text-xs font-medium text-gray-500 uppercase">Contas a Pagar</span>
                            <span className="text-sm font-semibold text-red-600">{uso.despesas} lançamento(s)</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </>
            )
          }
        </div>

        <div className="flex gap-3 px-6 py-4 border-t border-gray-100 justify-end">
          {(emUso || loading) ? (
            <button onClick={onClose} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">
              Entendido
            </button>
          ) : (
            <>
              <button onClick={onClose} className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={onConfirmar} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium">
                Excluir
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Painel de categorias ──────────────────────────────────────────────────────
function PainelCategoria({ tipo, categorias, onNova, onEditar, onExcluir }) {
  const isReceita = tipo === 'receita'
  const cor = isReceita ? 'bg-green-600' : 'bg-red-500'
  const corBtn = isReceita ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'
  const titulo = isReceita ? 'Categorias de Receita' : 'Categorias de Despesa'

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className={`${cor} px-5 py-3`}>
        <h2 className="text-sm font-bold text-white">{titulo}</h2>
      </div>
      <div className="p-4">
        <button onClick={() => onNova(tipo)}
          className={`flex items-center gap-2 px-3 py-2 ${corBtn} text-white rounded-lg text-sm font-medium mb-4`}>
          <Plus className="w-4 h-4" />
          + Nova Categoria de {isReceita ? 'Receita' : 'Despesa'}
        </button>

        {categorias.length === 0 ? (
          <p className="text-gray-400 text-sm py-4 text-center">Nenhuma categoria</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="pb-2 text-xs font-semibold text-gray-500 text-left">Nome</th>
                <th className="pb-2 text-xs font-semibold text-gray-500 text-left">Tipo</th>
                <th className="pb-2" />
              </tr>
            </thead>
            <tbody>
              {categorias.map(cat => (
                <tr key={cat.id} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="py-2.5 text-sm text-gray-700">{cat.nome}</td>
                  <td className="py-2.5">
                    <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                      {cat.is_sistema ? 'Padrão' : 'Personalizada'}
                    </span>
                  </td>
                  <td className="py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {/* Editar sempre disponível (incluindo padrão — só renomeia) */}
                      <button
                        onClick={() => onEditar(cat)}
                        title="Editar nome"
                        className="p-1 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      {/* Excluir sempre visível; o modal bloqueia se necessário */}
                      <button
                        onClick={() => onExcluir(cat)}
                        title={cat.is_sistema ? 'Categorias padrão não podem ser excluídas' : 'Excluir categoria'}
                        className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function Categorias() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [receitas, setReceitas] = useState([])
  const [despesas, setDespesas] = useState([])

  // Modal criar / editar
  const [modalCategoria, setModalCategoria] = useState(null) // { tipo, cat? }

  // Modal excluir
  const [modalExcluir, setModalExcluir] = useState(null) // { cat }
  const [usoCategoria, setUsoCategoria] = useState(null)  // { total, receitas, despesas }
  const [loadingUso, setLoadingUso] = useState(false)

  const carregar = async () => {
    if (!orgId) return
    try {
      const [r, d] = await Promise.all([
        gestaoService.listarCategorias(orgId, 'receita'),
        gestaoService.listarCategorias(orgId, 'despesa'),
      ])
      setReceitas(r.data || [])
      setDespesas(d.data || [])
    } catch {
      toast.error('Erro ao carregar categorias')
    }
  }

  useEffect(() => { carregar() }, [orgId])

  // ── Criar ou editar ────────────────────────────────────────────────────────
  const handleSalvar = async (dados) => {
    try {
      if (modalCategoria.cat) {
        await gestaoService.atualizarCategoria(orgId, modalCategoria.cat.id, { nome: dados.nome })
        toast.success('Categoria renomeada!')
      } else {
        await gestaoService.criarCategoria(orgId, dados)
        toast.success('Categoria criada!')
      }
      setModalCategoria(null)
      carregar()
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao salvar categoria')
    }
  }

  // ── Abrir modal editar ─────────────────────────────────────────────────────
  const handleEditar = (cat) => {
    setModalCategoria({ tipo: cat.tipo_categoria, cat })
  }

  // ── Abrir modal excluir (busca uso imediatamente) ──────────────────────────
  const handleExcluir = async (cat) => {
    setModalExcluir({ cat })
    setUsoCategoria(null)

    setLoadingUso(true)
    try {
      const res = await gestaoService.verificarUsoCategoria(orgId, cat.id)
      setUsoCategoria(res.data)
    } catch {
      setUsoCategoria({ total: 0, receitas: 0, despesas: 0 })
    } finally {
      setLoadingUso(false)
    }
  }

  // ── Confirmar exclusão ─────────────────────────────────────────────────────
  const confirmarExcluir = async () => {
    const cat = modalExcluir?.cat
    if (!cat) return
    try {
      await gestaoService.excluirCategoria(orgId, cat.id)
      toast.success('Categoria excluída!')
      setModalExcluir(null)
      setUsoCategoria(null)
      carregar()
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao excluir')
    }
  }

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold text-gray-800">Gestão de Categorias</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PainelCategoria
          tipo="receita"
          categorias={receitas}
          onNova={(tipo) => setModalCategoria({ tipo })}
          onEditar={handleEditar}
          onExcluir={handleExcluir}
        />
        <PainelCategoria
          tipo="despesa"
          categorias={despesas}
          onNova={(tipo) => setModalCategoria({ tipo })}
          onEditar={handleEditar}
          onExcluir={handleExcluir}
        />
      </div>

      {modalCategoria && (
        <ModalCategoria
          tipo={modalCategoria.tipo}
          categoriaEditando={modalCategoria.cat}
          onClose={() => setModalCategoria(null)}
          onSalvar={handleSalvar}
        />
      )}

      {modalExcluir && (
        <ModalExcluir
          categoria={modalExcluir.cat}
          uso={usoCategoria}
          loading={loadingUso}
          onClose={() => { setModalExcluir(null); setUsoCategoria(null) }}
          onConfirmar={confirmarExcluir}
        />
      )}
    </div>
  )
}
